import { Account } from './types';

/**
 * Determines if the current account is an organization account.
 * An account is considered an organization if it has child accounts
 * (other accounts where parentAccountId matches this account's accountId).
 *
 * @param accounts - Array of all accounts available to the user
 * @param activeTenantId - The ID of the currently active account/tenant
 * @returns true if the current account is an organization, false otherwise
 */
export const isOrganizationAccount = (
  accounts: Account[] | undefined,
  activeTenantId: string | undefined
): boolean => {
  if (!accounts || !activeTenantId) {
    return false;
  }

  // Check if any account has this activeTenantId as their parent
  // and is not self-referencing (parentAccountId !== accountId)
  return accounts.some(
    (account) => account.parentAccountId === activeTenantId && account.accountId !== activeTenantId
  );
};

/**
 * Gets all child accounts for a given organization account.
 *
 * @param accounts - Array of all accounts available to the user
 * @param organizationId - The ID of the organization account
 * @returns Array of child accounts belonging to the organization
 */
export const getOrganizationChildAccounts = (
  accounts: Account[] | undefined,
  organizationId: string | undefined
): Account[] => {
  if (!accounts || !organizationId) {
    return [];
  }

  return accounts.filter(
    (account) => account.parentAccountId === organizationId && account.accountId !== organizationId
  );
};

/**
 * Hook to get organization status and child accounts for the current active account.
 *
 * @param accounts - Array of all accounts from AuthContext
 * @param activeTenantId - Active tenant ID from AuthContext
 * @returns Object with organization status and child accounts
 */
export const useOrganizationStatus = (
  accounts: Account[] | undefined,
  activeTenantId: string | undefined
) => {
  const isOrg = isOrganizationAccount(accounts, activeTenantId);
  const childAccounts = getOrganizationChildAccounts(accounts, activeTenantId);

  return {
    isOrganizationAccount: isOrg,
    childAccounts,
    hasChildAccounts: childAccounts.length > 0,
  };
};
